import {
  forwardRef,
  Fragment,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Modal, DataStateHandler, Button } from "@common";
import { formatMoney } from "@utils";
import { getDdsTrendDirection } from "@utils/associateFee";
import api from "@api/api";
import { adminEndpoints, clinicEndpoints } from "@api/endpoints";
import { buildUrlWithParams } from "@api/queryHelpers";

const DDS_COMPARISON_MONTH_COUNT = 6;

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Adapt the backend `breakdown` payload into the flat shape this modal renders.
const getCalculationFromBreakdown = (row) => {
  const b = row?.breakdown || {};
  const ded = Array.isArray(b.deductions) ? b.deductions : [];
  const byKey = (key) => toNum(ded.find((d) => d?.key === key)?.value);
  return {
    draw: toNum(b.dds_draw),
    basis: toNum(b.basis),
    basisLabel: b.basis_label || "Collection",
    lab: byKey("lab_cost"),
    mat: byKey("material_cost"),
    inv: byKey("invisalign_cost"),
    anes: byKey("anesthesia_fee"),
    anesg: byKey("anesthesiologist_fee"),
    fac: byKey("facility_fee"),
    bot: byKey("botox"),
    net: toNum(b.net_after_deductions),
    pct: toNum(b.fee_percentage),
  };
};

const shiftMonth = (monthValue, offset) => {
  if (!monthValue) return null;
  const [year, monthNumber] = String(monthValue).split("-").map(Number);
  if (!year || !monthNumber) return null;
  const date = new Date(Date.UTC(year, monthNumber - 1 + offset, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
};

const formatMonthLabel = (monthValue) => {
  if (!monthValue) return "-";
  const [year, monthNumber] = String(monthValue).split("-").map(Number);
  if (!year || !monthNumber) return monthValue;
  return new Date(Date.UTC(year, monthNumber - 1, 1)).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

const isRequestCanceled = (error) =>
  error?.code === "ERR_CANCELED" ||
  error?.name === "CanceledError" ||
  error?.message === "canceled";

async function fetchCompensationRows({ clinicCode, filters, signal }) {
  const url = buildUrlWithParams(
    clinicCode
      ? clinicEndpoints.GET_MONTHLY_COMPENSATION_BY_CLINIC(clinicCode)
      : adminEndpoints.GET_ALL_MONTHLY_COMPENSATIONS,
    { practitioner_type: "dds", limit: 10000, offset: 0, ...filters },
    { clean: true },
  );
  const response = await api.get(url, { signal });
  return response.data?.results ?? [];
}

function buildHistoryRows(row, rawHistory, currentMonth) {
  const months = Array.from({ length: DDS_COMPARISON_MONTH_COUNT }, (_, i) =>
    shiftMonth(currentMonth, -i),
  );
  const byMonth = new Map(rawHistory.map((r) => [r.month, r]));
  byMonth.set(currentMonth, { ...row, month: currentMonth });

  return months
    .map((monthValue) => {
      const source = byMonth.get(monthValue);
      if (!source) return null;
      const calculation = getCalculationFromBreakdown(source);
      const prevMonth = shiftMonth(monthValue, -1);
      const prevRow = prevMonth ? byMonth.get(prevMonth) : null;
      const prevDraw = prevRow ? getCalculationFromBreakdown(prevRow).draw : null;
      return {
        ...source,
        month: monthValue,
        calculation,
        previousDraw: prevDraw,
        trendDirection: getDdsTrendDirection(calculation.draw, prevDraw),
      };
    })
    .filter(Boolean);
}

const CLOSE_ANIMATION_MS = 200;

const DdsComparisonModal = forwardRef(function DdsComparisonModal(_props, ref) {
  const [isOpen, setIsOpen] = useState(false);
  const [displayRow, setDisplayRow] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedMonths, setExpandedMonths] = useState(() => new Set());
  const abortRef = useRef(null);
  const closeTimerRef = useRef(null);

  const close = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsOpen(false);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setDisplayRow(null);
      setHistoryRows([]);
      setError(null);
      setExpandedMonths(new Set());
      closeTimerRef.current = null;
    }, CLOSE_ANIMATION_MS);
  }, []);

  const open = useCallback((row) => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setDisplayRow(row);
    setIsOpen(true);
    setHistoryRows([]);
    setIsLoading(true);
    setError(null);
    setExpandedMonths(new Set());

    const currentMonth = row.month;
    const monthFrom = shiftMonth(currentMonth, -(DDS_COMPARISON_MONTH_COUNT - 1));

    fetchCompensationRows({
      clinicCode: row.clinic,
      filters: {
        practitioner_id: row.practitioner_id,
        month_from: monthFrom,
        month_to: currentMonth,
      },
      signal: controller.signal,
    })
      .then((rawRows) => {
        if (controller.signal.aborted) return;
        const normalized = buildHistoryRows(row, rawRows, currentMonth);
        setHistoryRows(normalized);
        setExpandedMonths(normalized[0] ? new Set([normalized[0].month]) : new Set());
      })
      .catch((err) => {
        if (isRequestCanceled(err)) return;
        setError("Failed to load DDS comparison history.");
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          abortRef.current = null;
        }
      });
  }, []);

  useImperativeHandle(ref, () => ({ open, close }), [open, close]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    },
    [],
  );

  const toggle = useCallback((monthValue) => {
    setExpandedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(monthValue)) next.delete(monthValue);
      else next.add(monthValue);
      return next;
    });
  }, []);

  if (!displayRow) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title={`DDS Comparison - ${displayRow.practitioner_name}`}
      size="xl"
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-palette-slate-soft px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-palette-slate-primary">
              {displayRow.practitioner_name}
            </p>
            <p className="text-xs text-palette-slate-secondary">
              {displayRow.clinic} · {formatMonthLabel(displayRow.month)}
            </p>
          </div>
          <div className="text-xs text-palette-slate-secondary">
            Showing current month plus previous {DDS_COMPARISON_MONTH_COUNT - 1} months
          </div>
        </div>

        <DataStateHandler
          isLoading={isLoading}
          error={error ? new Error(error) : null}
          isEmpty={!isLoading && !error && historyRows.length === 0}
          loadingMessage="Loading DDS comparison..."
          emptyMessage="No DDS history found for this practitioner."
          showLoadingSpinner={true}
        >
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-palette-slate-divider text-sm">
              <thead className="bg-palette-slate-soft">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-palette-slate-heading">
                    Month
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-palette-slate-heading">
                    DDS Draw
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-palette-slate-heading">
                    Vs Previous
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-palette-slate-heading">
                    Calculation
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-palette-slate-divider bg-white">
                {historyRows.map((historyRow) => {
                  const calculation = historyRow.calculation;
                  const isExpanded = expandedMonths.has(historyRow.month);
                  const difference =
                    historyRow.previousDraw === null
                      ? null
                      : calculation.draw - historyRow.previousDraw;
                  const deductions = [
                    { label: "Lab", value: calculation.lab },
                    { label: "Material", value: calculation.mat },
                    { label: "Invisalign", value: calculation.inv },
                    { label: "Anesthesia", value: calculation.anes },
                    { label: "Anesthesiologist", value: calculation.anesg },
                    { label: "Facility", value: calculation.fac },
                    { label: "Botox", value: calculation.bot },
                  ];

                  return (
                    <Fragment key={historyRow.month}>
                      <tr className="align-top">
                        <td className="px-4 py-3 font-medium text-palette-slate-primary">
                          {formatMonthLabel(historyRow.month)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span
                              className={`font-semibold ${
                                calculation.draw > 0
                                  ? "text-palette-emerald-hover"
                                  : "text-palette-rose-badge"
                              }`}
                            >
                              {formatMoney(calculation.draw)}
                            </span>
                            {historyRow.trendDirection && (
                              <span
                                className={
                                  historyRow.trendDirection === "up"
                                    ? "text-palette-emerald-badge"
                                    : historyRow.trendDirection === "down"
                                      ? "text-palette-rose-badge"
                                      : "text-palette-slate-placeholder"
                                }
                              >
                                <svg
                                  className="h-4 w-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d={
                                      historyRow.trendDirection === "up"
                                        ? "M12 19V5m0 0l-5 5m5-5l5 5"
                                        : historyRow.trendDirection === "down"
                                          ? "M12 5v14m0 0l-5-5m5 5l5-5"
                                          : "M5 12h14"
                                    }
                                  />
                                </svg>
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-palette-slate-badge">
                          {difference === null ? (
                            "-"
                          ) : difference === 0 ? (
                            "No change"
                          ) : (
                            <span
                              className={
                                difference > 0
                                  ? "text-palette-emerald-hover"
                                  : "text-palette-rose-badge"
                              }
                            >
                              {difference > 0 ? "+" : ""}
                              {formatMoney(difference)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            variant="link"
                            size="xs"
                            onClick={() => toggle(historyRow.month)}
                            className="!h-auto !p-0"
                          >
                            {isExpanded ? "Hide Calculation" : "Show Calculation"}
                          </Button>
                        </td>
                      </tr>
                      <tr className={isExpanded ? "" : "hidden"}>
                        <td className="px-4 pb-4" colSpan={4}>
                          <div className="rounded-xl border border-palette-slate-divider bg-palette-slate-soft p-4">
                            <div className="grid gap-3 md:grid-cols-2">
                              <div className="space-y-2 text-sm text-palette-slate-heading">
                                <div className="flex items-center justify-between gap-4">
                                  <span className="font-medium text-palette-slate-primary">
                                    {calculation.basisLabel}
                                  </span>
                                  <span>{formatMoney(calculation.basis)}</span>
                                </div>
                                {deductions.map((item) => (
                                  <div
                                    key={item.label}
                                    className="flex items-center justify-between gap-4"
                                  >
                                    <span>{item.label}</span>
                                    <span>{formatMoney(item.value)}</span>
                                  </div>
                                ))}
                              </div>
                              <div className="space-y-2 border-t border-palette-slate-divider pt-3 text-sm text-palette-slate-heading md:border-l md:border-t-0 md:pl-4 md:pt-0">
                                <div className="flex items-center justify-between gap-4">
                                  <span className="font-medium text-palette-slate-primary">
                                    Net
                                  </span>
                                  <span>{formatMoney(calculation.net)}</span>
                                </div>
                                <div className="flex items-center justify-between gap-4">
                                  <span className="font-medium text-palette-slate-primary">
                                    Fee %
                                  </span>
                                  <span>{calculation.pct.toFixed(2)}%</span>
                                </div>
                                <div className="flex items-center justify-between gap-4 border-t border-palette-slate-divider pt-2">
                                  <span className="font-semibold text-palette-slate-primary">
                                    DDS Draw
                                  </span>
                                  <span
                                    className={`font-semibold ${
                                      calculation.draw > 0
                                        ? "text-palette-emerald-hover"
                                        : "text-palette-rose-badge"
                                    }`}
                                  >
                                    {formatMoney(calculation.draw)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </DataStateHandler>
      </div>
    </Modal>
  );
});

export default DdsComparisonModal;
